// Renders a single blog section based on its type/subtype using the
// admin-configured Visual Editor global styles. Used by both the BlogEditor
// preview pane and the public BlogReader.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Lightbulb, AlertTriangle, ArrowRight, Route as RouteIconR, Sparkles as SparklesIcon } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SUBTYPE_META, veStyle } from "./sectionTypes";
import { useAuth } from "@/contexts/AuthContext";
import { useReads } from "@/contexts/ReadsContext";
import { ReadBadge, PathwayProgress } from "@/components/ReadIndicators";
import { imgStyleClass, bgStyleAttrs, pickThemed } from "@/components/imageStyleFields";
import { useTheme } from "@/contexts/ThemeContext";
import TipJarCard from "@/components/TipJarCard";
import BibliographyRef from "@/components/BibliographyRef";

const CALLOUT_ICONS = { pearl: Sparkles, tip: Lightbulb, caution: AlertTriangle };

// Track whether the <html> element currently has the .dark class so the
// SectionRenderer can pick the light or dark palette of each subtype's
// VE style cell. Uses a MutationObserver so the callouts re-render when the
// user toggles the site-wide theme.
function useIsDark() {
  const [dark, setDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark"));
  useEffect(() => {
    if (typeof document === "undefined") return;
    const obs = new MutationObserver(() =>
      setDark(document.documentElement.classList.contains("dark")));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

// Merge a style cell with its `.dark` overrides when dark mode is active.
function resolveStyle(cell, isDark) {
  if (!cell) return {};
  if (isDark && cell.dark && typeof cell.dark === "object") return { ...cell, ...cell.dark };
  return cell;
}

export default function SectionRenderer({ section, veSettings, blogDisplay, forceMode, preview = false }) {
  // Backward-compat: sections created before the subtype system default to
  // editorial prose so they render exactly like they used to.
  const s = { ...section, type: section?.type || "editorial", subtype: section?.subtype || "prose" };
  const meta = SUBTYPE_META[s.subtype] || SUBTYPE_META.prose;
  const isDark = useIsDark();
  // `forceMode` lets the Visual Editor admin panel preview either palette
  // regardless of the current site theme.
  const effectiveDark = forceMode === "dark" ? true : forceMode === "light" ? false : isDark;
  const cellRaw = veSettings?.[meta.type]?.[s.subtype] || {};
  const style = resolveStyle(cellRaw, effectiveDark);
  const box = veStyle(style);
  const HeadingTag = s.level === 1 ? "h1" : s.level === 3 ? "h3" : "h2";
  const headingStyle = { fontWeight: style.heading_bold === false ? 400 : undefined, color: style.text };

  // ----- Editorial: prose -----
  if (s.subtype === "prose") {
    return (
      <>
        {s.heading && <HeadingTag style={headingStyle}>{s.heading}</HeadingTag>}
        <div dangerouslySetInnerHTML={{ __html: s.html || "<p><em class='text-slate-400'>Empty section</em></p>" }} />
      </>
    );
  }

  // ----- Editorial: buy-me-a-coffee tip jar -----
  if (s.subtype === "tip_jar") {
    const d = s.data || {};
    const amts = Array.isArray(d.amounts_cents) && d.amounts_cents.length
      ? d.amounts_cents.filter(n => Number.isFinite(n))
      : [300, 500, 1000];
    return (
      <div className="my-8">
        <TipJarCard
          heading={(s.heading && s.heading.trim()) || d.heading || "Enjoying Hospice Synopsis?"}
          body={d.body || "This site is free, ad-light, and built by one person. If it's helped you, you can say thanks with a coffee."}
          buttonLabel={d.button_label || "Buy me a coffee"}
          amountsCents={amts}
          allowCustom={d.allow_custom !== false}
        />
      </div>
    );
  }

  // ----- Editorial: callouts (pearl / tip / caution) -----
  if (["pearl", "tip", "caution"].includes(s.subtype)) {
    const CalloutIcon = CALLOUT_ICONS[s.subtype];
    const label = (s.heading && s.heading.trim()) || `Clinical ${meta.label}`;
    return (
      <div className="my-8 p-6" style={{ ...box, textAlign: undefined }}>
        <div className="flex items-center gap-2 mb-3">
          {style.show_icon !== false && (
            <CalloutIcon className="w-4 h-4 shrink-0" style={{ color: style.accent }} />
          )}
          <div className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: style.accent }}>
            {label}
          </div>
        </div>
        <div
          className="callout-body prose-invert [&_p]:m-0 [&_p+p]:mt-3 [&_a]:underline [&_strong]:font-semibold"
          style={{ color: style.text, textAlign: style.align || "left", fontSize: "1.0625rem", lineHeight: 1.6 }}
          dangerouslySetInnerHTML={{ __html: s.html || "<p><em>Empty callout</em></p>" }}
        />
      </div>
    );
  }

  // ----- Editorial: 3-2-1 Summary (horizontal 3-card block) -----
  if (s.subtype === "summary_321") {
    const d = s.data || {};
    const insights = Array.isArray(d.insights) ? d.insights.filter(Boolean) : [];
    const actions = Array.isArray(d.actions) ? d.actions.filter(Boolean) : [];
    const question = (d.question || "").trim();
    const headingText = (s.heading && s.heading.trim()) || "3-2-1 Summary";
    const showNumber = d.show_number !== false;
    const labelInsights = (d.label_insights || "Insight").trim();
    const labelActions  = (d.label_actions  || "Action").trim();
    const labelQuestion = (d.label_question || "Question").trim();
    const columns = Math.max(1, Math.min(3, Number.isFinite(d.columns) ? d.columns : 3));
    // Palette per column — sky / emerald / amber by default, admin overridable.
    const paletteMap = {
      sky:     { border: "border-sky-100 dark:border-sky-900",       bg: "bg-sky-50 dark:bg-sky-950/20",     marker: "marker:text-sky-700" },
      emerald: { border: "border-emerald-100 dark:border-emerald-900", bg: "bg-emerald-50 dark:bg-emerald-950/20", marker: "marker:text-emerald-700" },
      amber:   { border: "border-amber-100 dark:border-amber-900",    bg: "bg-amber-50 dark:bg-amber-950/20",   marker: "marker:text-amber-700" },
      rose:    { border: "border-rose-100 dark:border-rose-900",      bg: "bg-rose-50 dark:bg-rose-950/20",     marker: "marker:text-rose-700" },
      violet:  { border: "border-violet-100 dark:border-violet-900",  bg: "bg-violet-50 dark:bg-violet-950/20", marker: "marker:text-violet-700" },
      slate:   { border: "border-slate-200 dark:border-slate-700",    bg: "bg-slate-50 dark:bg-slate-900/40",   marker: "marker:text-slate-700" },
    };
    const colorInsights = paletteMap[d.color_insights || "sky"]     || paletteMap.sky;
    const colorActions  = paletteMap[d.color_actions  || "emerald"] || paletteMap.emerald;
    const colorQuestion = paletteMap[d.color_question || "amber"]   || paletteMap.amber;
    // Grid template — 1 / 2 / 3 columns. Always horizontal at the picked count
    // (no responsive stacking) so the layout defaults to a row across the article.
    const colClass = columns === 1 ? "grid-cols-1"
                   : columns === 2 ? "grid-cols-2"
                                   : "grid-cols-3";

    if (!insights.length && !actions.length && !question) {
      return (
        <section className="my-8">
          <HeadingTag style={headingStyle}>{headingText}</HeadingTag>
          <p className="text-slate-400 italic text-sm">Empty 3-2-1 Summary — add insights, actions, and a question in the editor.</p>
        </section>
      );
    }
    return (
      <section className="my-10">
        <div className="text-xs font-bold uppercase tracking-[0.24em] text-sky-700 dark:text-sky-400 mb-4">
          {headingText}
        </div>
        <div className={`grid ${colClass} gap-4`}>
          {insights.length > 0 && (
            <div className={`rounded-md border ${colorInsights.border} ${colorInsights.bg} p-5`}>
              <div className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {showNumber && `${insights.length} `}{labelInsights}{insights.length === 1 ? "" : "s"}
              </div>
              <ul className={`space-y-2 text-sm text-slate-700 dark:text-slate-300 list-decimal pl-5 ${colorInsights.marker} marker:font-semibold`}>
                {insights.map((it, i) => <li key={i} className="leading-relaxed">{it}</li>)}
              </ul>
            </div>
          )}
          {actions.length > 0 && (
            <div className={`rounded-md border ${colorActions.border} ${colorActions.bg} p-5`}>
              <div className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {showNumber && `${actions.length} `}{labelActions}{actions.length === 1 ? "" : "s"}
              </div>
              <ul className={`space-y-2 text-sm text-slate-700 dark:text-slate-300 list-decimal pl-5 ${colorActions.marker} marker:font-semibold`}>
                {actions.map((it, i) => <li key={i} className="leading-relaxed">{it}</li>)}
              </ul>
            </div>
          )}
          {question && (
            <div className={`rounded-md border ${colorQuestion.border} ${colorQuestion.bg} p-5`}>
              <div className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {showNumber && "1 "}{labelQuestion}
              </div>
              <p className="text-lg italic text-slate-800 dark:text-slate-200 leading-snug">
                &ldquo;{question}&rdquo;
              </p>
            </div>
          )}
        </div>
      </section>
    );
  }

  // ----- Editorial: Bibliography (numbered list) -----
  if (s.subtype === "bibliography") {
    const d = s.data || {};
    const items = Array.isArray(d.items) ? d.items.filter(Boolean) : [];
    const headingText = (s.heading && s.heading.trim()) || "Bibliography";
    // Admin-configured URL rendering. When disabled, URLs render as plain text
    // (same as before this feature). Otherwise wrap detected URLs in <a> tags
    // per the chosen visual style, always opening in a new tab.
    const urlRender = blogDisplay?.bibliography_url_render !== false;
    const urlStyleId = blogDisplay?.bibliography_url_style || "subtle";
    if (items.length === 0) {
      return (
        <section className="my-8">
          <HeadingTag style={headingStyle}>{headingText}</HeadingTag>
          <p className="text-slate-400 italic text-sm">Empty bibliography — add references in the editor.</p>
        </section>
      );
    }
    return (
      <section className="my-10">
        <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 mb-4">
          {headingText}
        </div>
        <ol className="space-y-3 list-decimal pl-6 text-sm text-slate-700 dark:text-slate-300">
          {items.map((ref, i) => (
            <li key={i} data-testid={`bibliography-item-${i}`} className="leading-relaxed pl-1">
              <BibliographyRef text={ref} enabled={urlRender} styleId={urlStyleId}/>
            </li>
          ))}
        </ol>
      </section>
    );
  }

  // ----- Media: image -----
  if (s.subtype === "image") {
    const d = s.data || {};
    return (
      <figure className="my-8" style={{ textAlign: style.align || "center" }}>
        {s.heading && <HeadingTag style={headingStyle}>{s.heading}</HeadingTag>}
        {d.image_url
          ? <img src={d.image_url} alt={d.alt || ""}
                 className={`inline-block max-w-full ${imgStyleClass(d, { defaults: { fit: "scale_down", height: "auto", radius: "md" } })}`}
                 style={{ borderColor: style.border !== "transparent" ? style.border : undefined,
                          borderWidth: style.border !== "transparent" ? 1 : 0,
                          borderStyle: style.border !== "transparent" ? "solid" : undefined }} />
          : <div className="text-slate-400 italic py-8 border border-dashed border-slate-300 rounded">No image URL set</div>}
        {d.caption && style.show_caption !== false && (
          <figcaption className="mt-2 text-sm text-slate-500 italic">{d.caption}</figcaption>
        )}
      </figure>
    );
  }

  // ----- Media: image + text -----
  if (s.subtype === "image_and_text") {
    const d = s.data || {};
    const flip = d.image_position === "right";
    return (
      <section className="my-8">
        {s.heading && <HeadingTag style={headingStyle}>{s.heading}</HeadingTag>}
        <div className={`grid gap-6 items-start md:grid-cols-2 ${flip ? "md:[&>*:first-child]:order-2" : ""}`}>
          <div>
            {d.image_url
              ? <img src={d.image_url} alt={d.alt || ""}
                     className={`w-full ${imgStyleClass(d, { defaults: { fit: "cover", height: "auto", radius: "sm" } })}`}/>
              : <div className="text-slate-400 italic py-12 text-center border border-dashed border-slate-300 rounded">No image URL set</div>}
            {d.caption && style.show_caption !== false && (
              <div className="mt-2 text-sm text-slate-500 italic">{d.caption}</div>
            )}
          </div>
          <div className="callout-body" dangerouslySetInnerHTML={{ __html: s.html || "<p><em>Empty text</em></p>" }} />
        </div>
      </section>
    );
  }

  // ----- Media: image gallery -----
  if (s.subtype === "image_gallery") {
    const images = (s.data?.images) || [];
    return (
      <section className="my-8">
        {s.heading && <HeadingTag style={headingStyle}>{s.heading}</HeadingTag>}
        {images.length === 0 ? (
          <div className="text-slate-400 italic py-8 text-center border border-dashed border-slate-300 rounded">No images in gallery yet</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {images.map((im, i) => (
              <figure key={i} className="m-0">
                <img src={im.url} alt={im.alt || ""} className="w-full h-40 object-cover"
                     style={{ borderRadius: `${style.radius ?? 8}px` }}/>
                {im.caption && style.show_caption !== false && (
                  <figcaption className="mt-1 text-xs text-slate-500 italic">{im.caption}</figcaption>
                )}
              </figure>
            ))}
          </div>
        )}
      </section>
    );
  }

  // ----- Data: table -----
  if (s.subtype === "table") {
    const d = s.data || {};
    const cols = d.columns || [];
    const rows = d.rows || [];
    return (
      <section className="my-8">
        {s.heading && <HeadingTag style={headingStyle}>{s.heading}</HeadingTag>}
        <div className="overflow-x-auto" style={box}>
          <table className="w-full text-sm" style={{ color: style.text }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${style.accent || "#0369a1"}` }}>
                {cols.map((c, i) => (
                  <th key={i} className="text-left px-3 py-2 font-semibold" style={{ color: style.accent }}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} style={{ borderBottom: `1px solid ${style.border !== "transparent" ? style.border : "#e2e8f0"}` }}>
                  {cols.map((_, ci) => (
                    <td key={ci} className="px-3 py-2">{r[ci] ?? ""}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {d.caption && style.show_caption !== false && (
          <div className="mt-2 text-sm text-slate-500 italic">{d.caption}</div>
        )}
      </section>
    );
  }

  // ----- Data: graph (bar / line / pie) -----
  if (s.subtype === "graph") {
    const d = s.data || {};
    const series = d.series || [];
    const type = d.chart_type || "bar";
    const values = series.map((x) => Number(x.value) || 0);
    const max = Math.max(1, ...values);
    const total = values.reduce((a, b) => a + b, 0) || 1;
    // Palette derived from the accent — used for pie slices and the line stroke.
    const palette = [
      style.accent,
      "#f97316", "#22c55e", "#a855f7", "#ec4899",
      "#14b8a6", "#eab308", "#3b82f6", "#ef4444", "#8b5cf6",
    ];

    let chart;
    if (type === "line") {
      // Simple SVG line chart.
      const W = 480, H = 200, P = 24;
      const step = series.length > 1 ? (W - 2 * P) / (series.length - 1) : 0;
      const pts = values.map((v, i) => {
        const x = P + i * step;
        const y = P + (1 - v / max) * (H - 2 * P);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(" ");
      chart = (
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" className="w-full h-56">
          {/* axes */}
          <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke={style.border !== "transparent" ? style.border : "#cbd5e1"} strokeWidth="1"/>
          <line x1={P} y1={P} x2={P} y2={H - P} stroke={style.border !== "transparent" ? style.border : "#cbd5e1"} strokeWidth="1"/>
          {/* line + points */}
          {series.length > 0 && <polyline fill="none" stroke={style.accent} strokeWidth="2" points={pts}/>}
          {series.map((pt, i) => {
            const x = P + i * step, y = P + (1 - (Number(pt.value)||0) / max) * (H - 2 * P);
            return <circle key={i} cx={x} cy={y} r="3.5" fill={style.accent}/>;
          })}
          {/* x-axis labels */}
          {series.map((pt, i) => {
            const x = P + i * step;
            return <text key={i} x={x} y={H - P + 14} textAnchor="middle" fontSize="10" fill={style.text}>{pt.label}</text>;
          })}
        </svg>
      );
    } else if (type === "pie") {
      const CX = 100, CY = 100, R = 90;
      let acc = 0;
      chart = (
        <div className="flex items-center gap-6 flex-wrap">
          <svg viewBox="0 0 200 200" className="w-48 h-48 shrink-0">
            {series.map((pt, i) => {
              const v = Number(pt.value) || 0;
              if (v <= 0) return null;
              const startA = (acc / total) * 2 * Math.PI;
              acc += v;
              const endA = (acc / total) * 2 * Math.PI;
              const x1 = CX + R * Math.sin(startA), y1 = CY - R * Math.cos(startA);
              const x2 = CX + R * Math.sin(endA),   y2 = CY - R * Math.cos(endA);
              const largeArc = endA - startA > Math.PI ? 1 : 0;
              const path = `M ${CX} ${CY} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
              return <path key={i} d={path} fill={palette[i % palette.length]} stroke={style.bg || "#fff"} strokeWidth="1"/>;
            })}
          </svg>
          <ul className="space-y-1 text-sm" style={{ color: style.text }}>
            {series.map((pt, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded" style={{ background: palette[i % palette.length] }}/>
                <span className="flex-1">{pt.label}</span>
                <span className="tabular-nums text-xs opacity-75">{pt.value} ({total ? Math.round((Number(pt.value)||0)/total*100) : 0}%)</span>
              </li>
            ))}
          </ul>
        </div>
      );
    } else {
      // Default: bar chart (kept as-is).
      chart = series.length === 0
        ? <div className="text-slate-400 italic py-6 text-center">No data points yet</div>
        : (
          <div className="space-y-2">
            {series.map((pt, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-24 text-xs shrink-0 truncate" style={{ color: style.text }}>{pt.label}</div>
                <div className="flex-1 h-6 rounded overflow-hidden" style={{ background: `${style.accent}22` }}>
                  <div className="h-full rounded transition-all" style={{ width: `${(Number(pt.value)||0) / max * 100}%`, background: style.accent }}/>
                </div>
                <div className="w-12 text-xs text-right tabular-nums" style={{ color: style.text }}>{pt.value}</div>
              </div>
            ))}
          </div>
        );
    }

    return (
      <section className="my-8">
        {s.heading && <HeadingTag style={headingStyle}>{s.heading}</HeadingTag>}
        <div className="p-4" style={box}>
          {series.length === 0 && type !== "bar"
            ? <div className="text-slate-400 italic py-6 text-center">No data points yet</div>
            : chart}
        </div>
        {d.caption && style.show_caption !== false && (
          <div className="mt-2 text-sm text-slate-500 italic">{d.caption}</div>
        )}
      </section>
    );
  }

  // ----- Layout: divider -----
  if (s.subtype === "divider") {
    const d = s.data || {};
    const thicknessPx = { thin: 1, medium: 2, thick: 4 }[d.thickness] || 1;
    const widthClass = { full: "w-full", wide: "w-3/4", narrow: "w-1/2", tight: "w-1/4" }[d.width] || "w-full";
    const alignClass = { left: "mr-auto", center: "mx-auto", right: "ml-auto" }[d.align] || "mx-auto";
    const marginClass = { sm: "my-4", md: "my-8", lg: "my-12", xl: "my-20" }[d.margin] || "my-8";
    const color = d.color || "#e2e8f0";
    const style = d.style || "solid";
    if (d.label) {
      // Label variant: two lines with the text centered between.
      return (
        <div className={`flex items-center gap-4 ${widthClass} ${alignClass} ${marginClass}`}>
          <div className="flex-1" style={{ borderTop: `${thicknessPx}px ${style} ${color}` }}/>
          <div className="text-sm uppercase tracking-[0.2em] text-slate-500 shrink-0">{d.label}</div>
          <div className="flex-1" style={{ borderTop: `${thicknessPx}px ${style} ${color}` }}/>
        </div>
      );
    }
    return (
      <hr className={`${widthClass} ${alignClass} ${marginClass} border-0`}
          style={{ borderTop: `${thicknessPx}px ${style} ${color}` }}/>
    );
  }

  // ----- Layout: custom grid -----
  if (s.subtype === "layout_grid") {
    return <LayoutGridSection d={s.data || {}}/>;
  }

  // ----- Dynamic: pulls data from backend and renders like the old Home page -----
  if (["hero", "pathways_grid", "recent_blogs", "glossary_teaser"].includes(s.subtype)) {
    return <DynamicSection section={s} preview={preview} forceMode={forceMode}/>;
  }

  // Fallback
  return <div className="text-slate-400 italic">Unknown section type: {s.subtype}</div>;
}

// =============================================================================
// Dynamic sections — pull live data from the API and render in the same style
// as the original hard-coded home page. Purely presentational: purposefully
// no dependency on AuthContext/ReadsContext so it also works cleanly inside
// the admin preview iframe.
// =============================================================================

const DEFAULT_HERO_IMAGE = "/images/hero.svg";

// Padding-Y class map for admin-configurable vertical padding on dynamic sections.
const PAD_Y = { none: "py-0", sm: "py-6", md: "py-14", lg: "py-20", xl: "py-28" };

const dynStyle = (d, theme = "light") => bgStyleAttrs(d, theme);
const padClass = (d) => PAD_Y[d.padding_y] ?? PAD_Y.md;

function useApiList(url, deps = []) {
  const [data, setData] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get(url).then(r => { if (!cancelled) setData(r.data || []); }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return data;
}

function DynamicSection({ section, preview = false, forceMode }) {
  const s = section;
  switch (s.subtype) {
    case "hero":            return <HeroDyn d={s.data || {}} preview={preview} forceMode={forceMode}/>;
    case "pathways_grid":   return <PathwaysDyn d={s.data || {}} preview={preview} forceMode={forceMode}/>;
    case "recent_blogs":    return <RecentBlogsDyn d={s.data || {}} preview={preview} forceMode={forceMode}/>;
    case "glossary_teaser": return <GlossaryTeaserDyn d={s.data || {}} preview={preview} forceMode={forceMode}/>;
    default:                return null;
  }
}

// Resolve the effective theme string a dynamic component should use for
// palette + cell.dark lookups. `forceMode` (passed by the Global Section
// Editor preview) always wins; otherwise fall back to the site-wide theme.
function useEffectiveTheme(forceMode) {
  const { resolvedMode } = useTheme() || {};
  if (forceMode === "dark" || forceMode === "light") return forceMode;
  return resolvedMode || "light";
}

// Sample pathways / blogs / glossary shown INSIDE the Global Section Editor
// preview when the API returns nothing yet or the site has no real data.
// Keeps the mini-preview from rendering as an empty placeholder or blank.
const PREVIEW_PATHWAYS_SAMPLE = [
  { pathway_id: "sample-a", slug: "sample-a", title: "Getting started",
    description: "A curated introduction to hospice care essentials.", resource_count: 4 },
  { pathway_id: "sample-b", slug: "sample-b", title: "Communication basics",
    description: "Language that centres presence, purpose, and clarity.", resource_count: 6 },
  { pathway_id: "sample-c", slug: "sample-c", title: "Symptom management",
    description: "Evidence-based approaches for common symptoms.", resource_count: 5 },
];
const PREVIEW_BLOGS_SAMPLE = [
  { blog_id: "sample-blog-1", slug: "sample-1", title: "Begin with the end in mind",
    excerpt: "How intention shapes the hospice conversation from day one." },
  { blog_id: "sample-blog-2", slug: "sample-2", title: "Never words in hospice",
    excerpt: "The phrases that erode trust — and the ones that build it." },
  { blog_id: "sample-blog-3", slug: "sample-3", title: "The BigR compass",
    excerpt: "A four-point framework for what matters most." },
];

function HeroDyn({ d, preview = false, forceMode }) {
  const theme = useEffectiveTheme(forceMode);
  const titleColor = pickThemed(d, "title_color", theme);
  const subtextColor = pickThemed(d, "subtext_color", theme);
  const layout = d.layout || "image-right";
  const eyebrow = d.eyebrow ?? "";
  const title = d.title ?? "Welcome";
  const description = d.description ?? "";
  const descriptionHtml = d.description_html || "";
  const ctaLabel = d.cta_label ?? "";
  const ctaHref = d.cta_href ?? "/pathways";
  const ctaStyle = d.cta_style || "primary";
  const cta2Label = d.cta2_label || "";
  const cta2Href = d.cta2_href || "/";
  const cta2Style = d.cta2_style || "ghost";
  const imageUrl = d.image_url || DEFAULT_HERO_IMAGE;
  const imageAlt = d.image_alt ?? "";
  const textAlign = d.text_align || "left";
  const alignCls = textAlign === "center" ? "text-center" : textAlign === "right" ? "text-right" : "text-left";

  // Buttons block — reused across layouts.
  const buttons = (ctaLabel || cta2Label) && (
    <div className={`mt-8 flex flex-wrap gap-3 ${textAlign === "center" ? "justify-center" : textAlign === "right" ? "justify-end" : ""}`}>
      {ctaLabel && (
        <Link to={ctaHref}>
          <Button className={heroBtnClass(ctaStyle)}>
            {ctaLabel} <ArrowRight className="ml-2 w-4 h-4"/>
          </Button>
        </Link>
      )}
      {cta2Label && (
        <Link to={cta2Href}>
          <Button className={heroBtnClass(cta2Style)} variant={cta2Style === "ghost" ? "ghost" : "default"}>
            {cta2Label}
          </Button>
        </Link>
      )}
    </div>
  );

  const textBlock = (
    <div className={alignCls}>
      {eyebrow && <div className="text-xs font-bold uppercase tracking-[0.24em] text-sky-700 dark:text-sky-400 mb-6">{eyebrow}</div>}
      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-slate-900 dark:text-slate-100"
          style={{ color: titleColor || undefined }}>{title}</h1>
      {descriptionHtml ? (
        <div className={`mt-6 text-lg max-w-2xl leading-relaxed prose prose-slate dark:prose-invert text-slate-700 dark:text-slate-300 ${textAlign === "center" ? "mx-auto" : textAlign === "right" ? "ml-auto" : ""}`}
             style={subtextColor ? { color: subtextColor } : undefined}
             dangerouslySetInnerHTML={{ __html: descriptionHtml }}/>
      ) : description ? (
        <p className={`mt-6 text-lg max-w-2xl leading-relaxed whitespace-pre-line text-slate-700 dark:text-slate-300 ${textAlign === "center" ? "mx-auto" : textAlign === "right" ? "ml-auto" : ""}`}
           style={subtextColor ? { color: subtextColor } : undefined}>{description}</p>
      ) : null}
      {buttons}
    </div>
  );

  const imgClass = `w-full block ${imgStyleClass(d, { defaults: { fit: "cover", height: "xl", radius: "lg" } })}`;
  const imageBlock = imageUrl ? <img src={imageUrl} alt={imageAlt} className={imgClass}/> : null;
  // Preview strips the full-bleed negative margins (`-mx-6 md:-mx-10 -mt-12`)
  // so the Hero fits inside a StyleCard cell.
  const outerCls = preview
    ? `rounded-md ${padClass({ ...d, padding_y: d.padding_y || "sm" })}`
    : `border-b border-slate-200 -mx-6 md:-mx-10 -mt-12 mb-8 ${padClass({ ...d, padding_y: d.padding_y || "xl" })}`;
  const outerStyle = dynStyle(d, theme);
  const innerMaxCls = preview ? "px-4" : "";

  // Layout composition.
  if (layout === "text-only") {
    return (
      <section data-testid="dyn-hero" className={outerCls} style={outerStyle}>
        <div className="max-w-4xl mx-auto px-6 md:px-10">{textBlock}</div>
      </section>
    );
  }
  if (layout === "image-only") {
    return (
      <section data-testid="dyn-hero" className={outerCls} style={outerStyle}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">{imageBlock}</div>
      </section>
    );
  }
  if (layout === "centered") {
    return (
      <section data-testid="dyn-hero" className={outerCls} style={outerStyle}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className={`${alignCls} text-center`}>{textBlock}</div>
          {imageBlock && <div className="mt-10">{imageBlock}</div>}
        </div>
      </section>
    );
  }
  if (layout === "image-top" || layout === "image-bottom") {
    const rev = layout === "image-bottom";
    return (
      <section data-testid="dyn-hero" className={outerCls} style={outerStyle}>
        <div className={`max-w-7xl mx-auto px-6 md:px-10 flex flex-col ${rev ? "flex-col-reverse" : ""} gap-10`}>
          {imageBlock}
          {textBlock}
        </div>
      </section>
    );
  }
  if (layout === "overlay") {
    return (
      <section data-testid="dyn-hero" className={`${outerCls} relative overflow-hidden`}
               style={{ ...outerStyle, minHeight: "480px" }}>
        {imageUrl && (
          <img src={imageUrl} alt={imageAlt}
               className="absolute inset-0 w-full h-full object-cover"/>
        )}
        <div className="absolute inset-0 bg-slate-900/50"/>
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 py-20 text-white">
          <div className={alignCls} style={{ color: "white" }}>
            {eyebrow && <div className="text-xs font-bold uppercase tracking-[0.24em] text-sky-200 mb-6">{eyebrow}</div>}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
                style={{ color: titleColor || "white" }}>{title}</h1>
            {descriptionHtml ? (
              <div className="mt-6 text-lg max-w-2xl leading-relaxed prose prose-invert"
                   style={{ color: subtextColor || "rgba(255,255,255,0.9)" }}
                   dangerouslySetInnerHTML={{ __html: descriptionHtml }}/>
            ) : description ? (
              <p className="mt-6 text-lg max-w-2xl leading-relaxed whitespace-pre-line"
                 style={{ color: subtextColor || "rgba(255,255,255,0.9)" }}>{description}</p>
            ) : null}
            {buttons}
          </div>
        </div>
      </section>
    );
  }
  // image-left / image-right (default) — 2-column split.
  const reversed = layout === "image-left";
  return (
    <section data-testid="dyn-hero" className={outerCls} style={outerStyle}>
      <div className={`max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center ${reversed ? "" : ""}`}>
        <div className={`lg:col-span-7 ${reversed ? "lg:order-2" : ""}`}>{textBlock}</div>
        <div className={`lg:col-span-5 ${reversed ? "lg:order-1" : ""}`}>{imageBlock}</div>
      </div>
    </section>
  );
}

// Button class helper reused by hero + layout_grid cells.
function heroBtnClass(style) {
  if (style === "secondary") return "bg-white text-sky-700 hover:bg-slate-100 border border-sky-200 h-11 px-6";
  if (style === "ghost")     return "bg-transparent text-sky-700 hover:bg-sky-50 h-11 px-6";
  return "bg-sky-700 hover:bg-sky-600 text-white h-11 px-6";
}

// ---------------------------------------------------------------------------
// LayoutGrid — a fully editable grid of cells.
// ---------------------------------------------------------------------------
const LG_GAP = { sm: "gap-3", md: "gap-6", lg: "gap-10" };
const LG_PAD_CELL = { none: "p-0", sm: "p-3", md: "p-5", lg: "p-8" };
const LG_RADIUS = { none: "rounded-none", sm: "rounded", md: "rounded-md", lg: "rounded-lg", xl: "rounded-xl", full: "rounded-full" };
const LG_ALIGN_ITEMS = { stretch: "items-stretch", start: "items-start", center: "items-center", end: "items-end" };

function LayoutGridSection({ d }) {
  const { resolvedMode } = useTheme() || {};
  const theme = resolvedMode || "light";
  const preset = d.preset || "2-col-split";
  // Column count: preset defines it, except "custom" which uses d.columns.
  const presetCols = {
    "2-col-split": 2, "3-col-cards": 3, "4-up": 4,
    "image-left": 2, "image-right": 2, "hero-sidecar": 3, "custom": Number(d.columns) || 2,
  }[preset] || 2;
  const cols = Math.max(1, Math.min(6, presetCols));
  const cells = Array.isArray(d.cells) ? d.cells : [];
  const gapCls = LG_GAP[d.gap] || LG_GAP.md;
  const itemsCls = LG_ALIGN_ITEMS[d.align_items] || LG_ALIGN_ITEMS.stretch;
  const gridStyle = { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` };

  return (
    <section data-testid="dyn-layout-grid" className={`-mx-6 md:-mx-10 px-6 md:px-10 ${padClass(d)}`} style={dynStyle(d, theme)}>
      <div className="max-w-7xl mx-auto">
        <div className={`grid ${gapCls} ${itemsCls}`} style={gridStyle}>
          {cells.map((c) => (
            <LayoutGridCell key={c.id} c={c} maxCols={cols} theme={theme}/>
          ))}
        </div>
      </div>
    </section>
  );
}

function LayoutGridCell({ c, maxCols, theme = "light" }) {
  const span = Math.max(1, Math.min(maxCols, Number(c.span) || 1));
  const padCls = LG_PAD_CELL[c.padding] || LG_PAD_CELL.md;
  const radCls = LG_RADIUS[c.radius] || LG_RADIUS.sm;
  const alignCls = c.text_align === "center" ? "text-center" : c.text_align === "right" ? "text-right" : "text-left";
  const bg = pickThemed(c, "bg_color", theme);
  const style = { gridColumn: `span ${span} / span ${span}`, backgroundColor: bg || undefined };

  if (c.content_type === "image") {
    const img = c.image_url ? (
      <img src={c.image_url} alt={c.image_alt || ""} className={`w-full h-full object-cover ${radCls}`}/>
    ) : (
      <div className={`w-full aspect-video bg-slate-100 grid place-items-center text-slate-400 ${radCls}`}>No image</div>
    );
    return (
      <div style={style} className={`${padCls} ${radCls} ${alignCls}`}>
        {c.link_href ? <Link to={c.link_href} className="block">{img}</Link> : img}
      </div>
    );
  }
  if (c.content_type === "button") {
    return (
      <div style={style} className={`${padCls} ${radCls} ${alignCls} flex ${c.text_align === "center" ? "justify-center" : c.text_align === "right" ? "justify-end" : ""}`}>
        <Link to={c.href || "/"}>
          <Button className={heroBtnClass(c.style || "primary")}>{c.label || "Learn more"}</Button>
        </Link>
      </div>
    );
  }
  // text
  return (
    <div style={style} className={`${padCls} ${radCls} ${alignCls}`}>
      {c.heading && (
        <h3 className="font-heading text-xl md:text-2xl font-semibold text-slate-900 mb-2">{c.heading}</h3>
      )}
      {c.html && (
        <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: c.html }}/>
      )}
    </div>
  );
}

function PathwaysDyn({ d, preview = false, forceMode }) {
  const theme = useEffectiveTheme(forceMode);
  const fetched = useApiList("/pathways");
  const all = preview
    ? (fetched.length ? fetched : PREVIEW_PATHWAYS_SAMPLE)
    : fetched;
  // In preview we only show up to 3 cards regardless of admin limit — the
  // mini-preview container isn't wide enough to display more meaningfully.
  const limit = preview ? 3 : Math.max(1, Math.min(24, Number(d.limit) || 12));
  const pathways = all.slice(0, limit);
  const { user } = useAuth();
  const { isRead } = useReads();
  const eyebrow = d.eyebrow ?? "Guided reading";
  const heading = d.heading ?? "Pathways";
  const description = d.description ?? "";
  const useDefaultBg = !d.background_color && !d.background_image;
  // Preview strips the full-bleed negative margins (`-mx-6 md:-mx-10`) and
  // shrinks the vertical padding so the section fits inside a StyleCard cell.
  const outerCls = preview
    ? `${useDefaultBg ? "bg-slate-50 dark:bg-slate-900" : ""} rounded-md px-4 py-6`
    : `-mx-6 md:-mx-10 px-6 md:px-10 border-y border-slate-200 dark:border-slate-800 ${padClass(d)} ${
        useDefaultBg ? "bg-slate-50 dark:bg-slate-900" : ""
      }`;
  const gridCls = preview
    ? "grid grid-cols-1 sm:grid-cols-2 gap-3"
    : "grid md:grid-cols-2 lg:grid-cols-3 gap-6";
  const headingCls = preview
    ? "font-heading text-lg font-semibold text-slate-900 dark:text-slate-100"
    : "font-heading text-3xl md:text-4xl font-semibold text-slate-900 dark:text-slate-100";
  return (
    <section data-testid="dyn-pathways"
             className={outerCls}
             style={dynStyle(d, theme)}>
      <div className={preview ? "" : "max-w-7xl mx-auto"}>
      <div className={preview ? "flex items-end justify-between mb-4" : "flex items-end justify-between mb-8"}>
        <div>
          {eyebrow && <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400 mb-1">{eyebrow}</div>}
          <h2 className={headingCls}>{heading}</h2>
          {description && <p className={`mt-2 text-slate-700 dark:text-slate-300 max-w-2xl ${preview ? "text-xs" : ""}`}>{description}</p>}
        </div>
        <RouteIconR className={preview ? "w-4 h-4 text-slate-400 dark:text-slate-500" : "w-6 h-6 text-slate-400 dark:text-slate-500"}/>
      </div>
      {pathways.length === 0 ? (
        <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-8 text-center text-slate-500 dark:text-slate-400 text-sm">No pathways yet.</div>
      ) : (
        <div className={gridCls}>
          {pathways.map(p => {
            const total = p.resource_count ?? p.blog_ids?.length ?? 0;
            const readCount = user ? (p.blog_ids || []).filter(id => isRead(id)).length : 0;
            const inner = (
              <>
                <div className={preview
                  ? "font-heading text-sm font-semibold text-slate-900 dark:text-slate-100 group-hover:text-sky-700 dark:group-hover:text-sky-400"
                  : "font-heading text-xl font-semibold text-slate-900 dark:text-slate-100 group-hover:text-sky-700 dark:group-hover:text-sky-400"
                }>{p.title}</div>
                <p className={preview
                  ? "mt-1 text-[11px] text-slate-600 dark:text-slate-400 leading-snug line-clamp-2"
                  : "mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed line-clamp-3"
                }>{p.description || "A curated reading order."}</p>
                {!preview && user && total > 0 && (
                  <PathwayProgress read={readCount} total={total} size="sm" className="mt-4"/>
                )}
              </>
            );
            const cardCls = preview
              ? "group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-3 hover:border-sky-700 dark:hover:border-sky-500 transition-colors flex flex-col"
              : "group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-6 hover:border-sky-700 dark:hover:border-sky-500 transition-colors flex flex-col";
            return preview ? (
              <div key={p.pathway_id} className={cardCls}>{inner}</div>
            ) : (
              <Link key={p.pathway_id} to={`/pathways/${p.slug}`} className={cardCls}>{inner}</Link>
            );
          })}
        </div>
      )}
      </div>
    </section>
  );
}

function RecentBlogsDyn({ d, preview = false, forceMode }) {
  const theme = useEffectiveTheme(forceMode);
  const fetched = useApiList("/blogs");
  const blogs = preview
    ? (fetched.length ? fetched : PREVIEW_BLOGS_SAMPLE)
    : fetched;
  const { user } = useAuth();
  const { isRead } = useReads();
  const heading = d.heading ?? "Recently added";
  // Preview clamps to 3 so cards actually fit in a StyleCard cell.
  const limit = preview ? 3 : Math.max(1, Math.min(24, Number(d.limit) || 6));
  const list = blogs.slice(0, limit);
  // In preview we ALWAYS render (even with 0 items) so admins never see a
  // blank spot when there's no data yet — the samples above cover it.
  if (list.length === 0 && !preview) return null;
  const outerCls = preview
    ? `rounded-md px-4 py-6 ${!d.background_color && !d.background_image ? "" : ""}`
    : padClass(d);
  const gridCls = preview
    ? "grid grid-cols-1 sm:grid-cols-2 gap-3"
    : "grid md:grid-cols-2 lg:grid-cols-3 gap-6";
  const headingCls = preview
    ? "font-heading text-lg font-semibold text-slate-900 mb-4"
    : "font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-8";
  return (
    <section data-testid="dyn-recent-blogs" className={outerCls} style={dynStyle(d, theme)}>
      <div className={preview ? "" : "max-w-7xl mx-auto px-6 md:px-10"}>
      <h2 className={headingCls}>{heading}</h2>
      <div className={gridCls}>
        {list.map(b => {
          const done = user && isRead(b.blog_id);
          const cardCls = preview
            ? `border rounded-md p-3 hover:border-sky-700 transition-colors relative bg-white ${
                done ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200"}`
            : `border rounded-lg p-6 hover:border-sky-700 transition-colors relative bg-white ${
                done ? "border-emerald-200 bg-emerald-50/30 dark:bg-emerald-950/10 dark:border-emerald-900" : "border-slate-200"}`;
          const inner = (
            <>
              <div className={preview ? "flex items-center justify-between mb-1" : "flex items-center justify-between mb-2"}>
                <div className={preview ? "text-[9px] uppercase tracking-[0.2em] text-slate-500" : "text-xs uppercase tracking-[0.2em] text-slate-500"}>Blog</div>
                {!preview && done && <ReadBadge read withLabel={false}/>}
              </div>
              <div className={preview ? "font-heading text-sm font-semibold text-slate-900" : "font-heading text-lg font-semibold text-slate-900"}>{b.title}</div>
              <p className={preview ? "mt-1 text-[11px] text-slate-600 line-clamp-2" : "mt-2 text-sm text-slate-600 line-clamp-3"}>{b.excerpt}</p>
            </>
          );
          return preview ? (
            <div key={b.blog_id} className={cardCls}>{inner}</div>
          ) : (
            <Link key={b.blog_id} to={`/blogs/${b.slug}`} className={cardCls}>{inner}</Link>
          );
        })}
      </div>
      </div>
    </section>
  );
}

function GlossaryTeaserDyn({ d, preview = false, forceMode }) {
  const theme = useEffectiveTheme(forceMode);
  const eyebrow = d.eyebrow ?? "Reference";
  const heading = d.heading ?? "An always-open glossary";
  const description = d.description ?? "";
  const ctaLabel = d.cta_label ?? "Browse the glossary";
  const ctaHref = d.cta_href ?? "/glossary";
  const useDefaultBg = !d.background_color && !d.background_image;
  const outerCls = preview
    ? `rounded-md px-4 py-6 ${useDefaultBg ? "bg-sky-50 dark:bg-sky-950/40" : ""}`
    : `-mx-6 md:-mx-10 px-6 md:px-10 border-y border-slate-200 dark:border-slate-800 ${padClass(d)} ${
        useDefaultBg ? "bg-sky-50 dark:bg-sky-950/40" : ""
      }`;
  return (
    <section data-testid="dyn-glossary-teaser"
             className={outerCls}
             style={dynStyle(d, theme)}>
      <div className={preview
        ? "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
        : "max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6"}>
      <div className="max-w-2xl">
        {eyebrow && (
          <div className={preview
            ? "text-[10px] font-bold uppercase tracking-[0.24em] text-sky-700 mb-1 inline-flex items-center gap-1"
            : "text-xs font-bold uppercase tracking-[0.24em] text-sky-700 mb-3 inline-flex items-center gap-2"}>
            <SparklesIcon className={preview ? "w-3 h-3" : "w-3.5 h-3.5"}/> {eyebrow}
          </div>
        )}
        <h2 className={preview
          ? "font-heading text-lg font-semibold text-slate-900 dark:text-slate-100"
          : "font-heading text-2xl md:text-3xl font-semibold text-slate-900 dark:text-slate-100"}>{heading}</h2>
        {description && <p className={preview
          ? "mt-1 text-xs text-slate-700 dark:text-slate-300 whitespace-pre-line line-clamp-2"
          : "mt-2 text-slate-700 dark:text-slate-300 whitespace-pre-line"}>{description}</p>}
      </div>
      {ctaLabel && (
        preview ? (
          <Button className={preview ? "bg-sky-700 hover:bg-sky-600 h-8 px-3 text-xs shrink-0" : "bg-sky-700 hover:bg-sky-600 h-11 px-6"}>
            {ctaLabel} <ArrowRight className={preview ? "ml-1 w-3 h-3" : "ml-2 w-4 h-4"}/>
          </Button>
        ) : (
          <Link to={ctaHref}>
            <Button className="bg-sky-700 hover:bg-sky-600 h-11 px-6">
              {ctaLabel} <ArrowRight className="ml-2 w-4 h-4"/>
            </Button>
          </Link>
        )
      )}
      </div>
    </section>
  );
}
